import React, { useState } from 'react';
import { Phone, Mail, Send, MessageCircle, MapPin, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { companyInfo } from '../data/mock';

const Contato = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    dataCasamento: '',
    mensagem: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Monta a mensagem para o WhatsApp
    const mensagemWhatsApp = `Olá! Meu nome é *${formData.nome}*.

E-mail: ${formData.email}
Telefone: ${formData.telefone}
Data do Casamento: ${formData.dataCasamento || 'A definir'}

Mensagem:
${formData.mensagem}`;

    // Redireciona para o WhatsApp
    const whatsappUrl = `https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent(mensagemWhatsApp)}`;
    window.open(whatsappUrl, '_blank');
    
    toast({
      title: "Redirecionando para o WhatsApp!",
      description: "Continue a conversa por lá.",
    });

    // Reset form
    setFormData({
      nome: '',
      email: '',
      telefone: '',
      dataCasamento: '',
      mensagem: ''
    });
  };

  const whatsappMessage = encodeURIComponent(
    'Olá! Gostaria de solicitar um orçamento para música na minha cerimônia de casamento.'
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#DFD6CD]/20">
      {/* Header */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#DFD6CD] to-white">
        <div className="container mx-auto text-center">
          <div className="inline-block mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full text-sm font-medium text-[#920216] border border-[#CDAD1E]/30">
              <MessageCircle size={16} className="text-[#B30310]" />
              Fale Conosco
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#920216] mb-6">
            Entre em Contato
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Estamos prontos para ajudá-los a criar a trilha sonora perfeita para o seu grande dia.
            Preencha o formulário ou entre em contato diretamente pelo WhatsApp.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-[#CDAD1E]/30 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#DFD6CD] rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-[#B30310]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#920216] mb-2">Telefone</h3>
                      <a
                        href={`https://wa.me/${companyInfo.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-700 hover:text-[#B30310] transition-colors"
                      >
                        (61) 99587-8906
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#CDAD1E]/30 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#DFD6CD] rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#B30310]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#920216] mb-2">E-mail</h3>
                      <a
                        href={`mailto:${companyInfo.email}`}
                        className="text-gray-700 hover:text-[#B30310] transition-colors break-all"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#CDAD1E]/30 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#DFD6CD] rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#B30310]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#920216] mb-2">Localização</h3>
                      <p className="text-gray-700">Brasília e Entorno</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#CDAD1E]/30 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#DFD6CD] rounded-full flex items-center justify-center flex-shrink-0">
                      <Instagram className="text-[#B30310]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#920216] mb-2">Instagram</h3>
                      <a
                        href="https://www.instagram.com/harmonianupcialbsb/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-700 hover:text-[#B30310] transition-colors"
                      >
                        @harmonianupcialbsb
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* WhatsApp CTA */}
              <Button
                asChild
                className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white transition-all duration-300 shadow-lg hover:shadow-xl"
                size="lg"
              >
                <a
                  href={`https://wa.me/${companyInfo.whatsapp}?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2"
                >
                  <MessageCircle size={20} />
                  Falar no WhatsApp
                </a>
              </Button>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-[#CDAD1E]/30 shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[#920216] mb-6">
                    Envie sua Mensagem
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="nome" className="text-[#920216] font-medium">
                          Nome Completo *
                        </Label>
                        <Input
                          id="nome"
                          name="nome"
                          value={formData.nome}
                          onChange={handleChange}
                          required
                          placeholder="Seu nome"
                          className="border-[#CDAD1E]/30 focus:border-[#B30310]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#920216] font-medium">
                          E-mail *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="seu@email.com"
                          className="border-[#CDAD1E]/30 focus:border-[#B30310]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="telefone" className="text-[#920216] font-medium">
                          Telefone *
                        </Label>
                        <Input
                          id="telefone"
                          name="telefone"
                          type="tel"
                          value={formData.telefone}
                          onChange={handleChange}
                          required
                          placeholder="(61) 99999-9999"
                          className="border-[#CDAD1E]/30 focus:border-[#B30310]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="dataCasamento" className="text-[#920216] font-medium">
                          Data do Casamento
                        </Label>
                        <Input
                          id="dataCasamento"
                          name="dataCasamento"
                          type="date"
                          value={formData.dataCasamento}
                          onChange={handleChange}
                          className="border-[#CDAD1E]/30 focus:border-[#B30310]"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="mensagem" className="text-[#920216] font-medium">
                        Mensagem *
                      </Label>
                      <Textarea
                        id="mensagem"
                        name="mensagem"
                        value={formData.mensagem}
                        onChange={handleChange}
                        required
                        placeholder="Conte-nos sobre sua cerimônia e como podemos ajudar..."
                        rows={6}
                        className="border-[#CDAD1E]/30 focus:border-[#B30310] resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-[#B30310] hover:bg-[#920216] text-white transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      <Send size={20} className="mr-2" />
                      Enviar Mensagem
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contato;